import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCart, updateCartItemQuantity, removeFromCart, type CartItem } from '../../utils/cart';
import { useProducts } from '../../hooks/useProducts';

interface CartNotificationProps {
  item: CartItem | null;
  onClose: () => void;
}

export default function CartNotification({ item, onClose }: CartNotificationProps) {
  const [currentItem, setCurrentItem] = useState<CartItem | null>(null);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [stockWarning, setStockWarning] = useState('');
  const { products } = useProducts();

  const refreshItem = useCallback(() => {
    if (!item) return;
    const cart = getCart();
    const found = cart.find((c) => c.id === item.id);
    if (found) setCurrentItem(found);
  }, [item]);

  useEffect(() => {
    if (item) {
      setCurrentItem(item);
      setVisible(true);
      setAnimating(true);
      setStockWarning('');

      const refreshCart = () => {
        const cart = getCart();
        const found = cart.find((c) => c.id === item.id);
        if (found) setCurrentItem(found);
        else {
          setVisible(false);
          setTimeout(onClose, 300);
        }
      };
      window.addEventListener('cartUpdated', refreshCart);

      const timer = setTimeout(() => {
        setAnimating(false);
        setTimeout(() => {
          setVisible(false);
          setTimeout(onClose, 300);
        }, 50);
      }, 5000);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('cartUpdated', refreshCart);
      };
    }
  }, [item, onClose, refreshItem]);

  const handleQuantityChange = (newQty: number) => {
    if (!currentItem) return;
    
    // Find product stock
    const product = products.find(p => parseInt(p.id) === currentItem.id);
    const maxStock = product?.stock_quantity;
    
    if (newQty < 1) {
      removeFromCart(currentItem.id);
      setVisible(false);
      setTimeout(onClose, 300);
      return;
    }
    
    // Check stock limit
    if (maxStock !== undefined && newQty > maxStock) {
      setStockWarning(`Stock maximum atteint (${maxStock} unité${maxStock > 1 ? 's' : ''})`);
      setTimeout(() => setStockWarning(''), 3000);
      return;
    }
    
    const result = updateCartItemQuantity(currentItem.id, newQty, maxStock);
    if (!result.success && result.message) {
      setStockWarning(result.message);
      setTimeout(() => setStockWarning(''), 3000);
    }
  };

  const handleRemove = () => {
    if (!currentItem) return;
    removeFromCart(currentItem.id);
    setVisible(false);
    setTimeout(onClose, 300);
  };

  if (!currentItem || !visible) return null;

  const totalItems = getCart().reduce((sum, c) => sum + c.quantity, 0);
  const cartTotal = getCart().reduce((sum, c) => sum + c.price * c.quantity, 0);

  return (
    <div className={`fixed top-24 right-4 w-full sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-2xl z-50 transition-all duration-300 ${
      animating ? 'animate-slide-in' : 'opacity-0 translate-x-full'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#E8F5E9] border-b border-[#C8E6C9]">
        <div className="flex items-center gap-2">
          <i className="ri-check-line text-[#2E7D32] text-lg"></i>
          <span className="text-sm font-semibold text-[#2E7D32]">Ajouté au panier</span>
        </div>
        <button
          onClick={() => {
            setAnimating(false);
            setVisible(false);
            setTimeout(onClose, 300);
          }}
          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-white/60 transition-colors"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>

      {/* Stock Warning */}
      {stockWarning && (
        <div className="px-5 py-2 bg-orange-50 border-b border-orange-100">
          <div className="flex items-center gap-2 text-orange-600">
            <i className="ri-error-warning-line text-sm"></i>
            <span className="text-xs font-medium">{stockWarning}</span>
          </div>
        </div>
      )}

      {/* Product */}
      <div className="p-5">
        <div className="flex gap-4">
          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50">
            <img
              src={currentItem.image}
              alt={currentItem.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 truncate mb-1">{currentItem.name}</h4>
            <p className="text-sm text-gray-500 mb-3">${currentItem.price.toFixed(2)} chacun</p>

            {/* Quantity Controls */}
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => handleQuantityChange(currentItem.quantity - 1)}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-sm"></i>
                </button>
                <span className="w-10 h-8 flex items-center justify-center text-sm font-semibold text-gray-900 border-x border-gray-200">
                  {currentItem.quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(currentItem.quantity + 1)}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
              <span className="text-sm font-bold text-gray-900">
                ${(currentItem.price * currentItem.quantity).toFixed(2)}
              </span>
              <button
                onClick={handleRemove}
                className="ml-auto w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors cursor-pointer"
                title="Supprimer"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 pb-5">
        <div className="flex items-center justify-between mb-4 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500">{totalItems} article{totalItems > 1 ? 's' : ''} dans le panier</span>
          <span className="text-sm font-bold text-gray-900">Total: ${cartTotal.toFixed(2)}</span>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setAnimating(false);
              setVisible(false);
              setTimeout(onClose, 300);
            }}
            className="flex-1 py-2.5 border border-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            CONTINUER
          </button>
          <Link
            to="/payment"
            onClick={() => {
              setAnimating(false);
              setVisible(false);
              setTimeout(onClose, 300);
            }}
            className="flex-1 py-2.5 bg-[#2E7D32] text-white text-xs font-semibold rounded-lg hover:bg-[#1B5E20] transition-colors cursor-pointer whitespace-nowrap text-center flex items-center justify-center gap-1.5"
          >
            <i className="ri-shopping-cart-line text-sm"></i>
            VOIR PANIER
          </Link>
        </div>
      </div>
    </div>
  );
}
